"use client";

import { CheckCircle2, Package, ShieldCheck, ShoppingBag } from "lucide-react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import type { BundleData } from "@/lib/home/bundle-products";
import { useAddToCart } from "@/ui/components/cart/use-add-to-cart";
import { usePricingResolvePresentations } from "@/lib/pricing/use-pricing-resolve-presentations";
import { formatPrice, isValidLocale, defaultLocale } from "@/config/locale";
import { resolveCommerceSummary } from "@/lib/cart/commerce-summary";
import { computeTotals, pickVariantForQty, type Qty } from "./bundle-save-logic";

interface BundleSaveProps {
	bundle: BundleData;
	channel: string;
	locale: string;
}

const amountKey = (amount: number) => `${Math.round(amount * 1e6)}`;

export function BundleSave({ bundle, channel, locale }: BundleSaveProps) {
	const t = useTranslations("home.bundle");
	const tCart = useTranslations("cart");
	const [qty, setQty] = useState<Qty>(2);
	const [addWater, setAddWater] = useState(true);
	const [isPending, setIsPending] = useState(false);
	// Shared runner: the drawer opens with a transient line per bundle item instead
	// of flashing its empty state while the two adds run.
	const addToCart = useAddToCart();

	const totals = computeTotals(bundle, qty, addWater);
	const retaVariant = pickVariantForQty(bundle.reta, qty);
	const bacVariant = addWater ? pickVariantForQty(bundle.bac, 1) : null;
	const retaOutOfStock = !retaVariant || retaVariant.quantityAvailable <= 0;
	const summary = resolveCommerceSummary({
		scope: "selection",
		channel,
		currency: totals.currency,
		subtotalAmount: totals.priceReady ? totals.grand : Number.NaN,
		discountAmount: 0,
		resolvedTotalAmount: totals.priceReady ? totals.grand : Number.NaN,
		freeShippingBasisAmount: totals.selectionBasis,
		attached: { usesAttachedShipping: false, amount: 0 },
		delivery: {
			status: "unresolved",
			amountIsKnown: false,
			appliesToCurrentCart: false,
			shippingRequired: true,
		},
		freshness: totals.priceReady ? "ready" : "unavailable",
	});

	const singlePackUnit = bundle.reta[0]?.pricing?.price?.gross.amount;

	// Pre-resolve every amount that could be displayed across all state combinations.
	// The items list is derived from the bundle (stable across qty/addWater toggles) so
	// the hook fetches once.
	const resolveItems = useMemo(() => {
		const currency = bundle.reta[0]?.pricing?.price?.gross.currency ?? "EUR";
		const unit = bundle.reta[0]?.pricing?.price?.gross.amount ?? 0;
		const bacAmount = bundle.bac[0]?.pricing?.price?.gross.amount ?? 0;
		const amounts = new Set<number>([bacAmount]);
		const addAmount = (amount: number | null | undefined) => {
			if (typeof amount === "number" && Number.isFinite(amount) && amount >= 0) amounts.add(amount);
		};
		for (const q of [1, 2, 3] as Qty[]) {
			const variant = pickVariantForQty(bundle.reta, q);
			const retaTotal = variant?.pricing?.price?.gross.amount ?? 0;
			addAmount(retaTotal);
			addAmount(retaTotal / q);
			addAmount(retaTotal + bacAmount);
			addAmount(Math.max(0, unit * q - retaTotal));
			for (const includeWater of [false, true]) {
				const candidateTotals = computeTotals(bundle, q, includeWater);
				const candidateSummary = resolveCommerceSummary({
					scope: "selection",
					channel,
					currency: candidateTotals.currency,
					subtotalAmount: candidateTotals.priceReady ? candidateTotals.grand : Number.NaN,
					discountAmount: 0,
					resolvedTotalAmount: candidateTotals.priceReady ? candidateTotals.grand : Number.NaN,
					freeShippingBasisAmount: candidateTotals.selectionBasis,
					attached: { usesAttachedShipping: false, amount: 0 },
					delivery: {
						status: "unresolved",
						amountIsKnown: false,
						appliesToCurrentCart: false,
						shippingRequired: true,
					},
					freshness: candidateTotals.priceReady ? "ready" : "unavailable",
				});
				addAmount(candidateTotals.grand);
				addAmount(candidateSummary.shipping.amount);
				addAmount(candidateSummary.freeShipping.remaining);
				addAmount(candidateSummary.totalAmount);
			}
			if (q < 3) {
				const nextQty = (q + 1) as Qty;
				const nextReta = pickVariantForQty(bundle.reta, nextQty);
				if (nextReta) {
					const nextPerUnit = (nextReta.pricing?.price?.gross.amount ?? 0) / nextQty;
					amounts.add(Math.max(0, retaTotal / q - nextPerUnit));
				}
			}
		}
		return [...amounts].map((amount) => ({ id: amountKey(amount), amount, currency }));
	}, [bundle, channel]);

	const presMap = usePricingResolvePresentations(locale, channel, resolveItems);

	const formatMoney = (amount: number) => {
		const pres = presMap.get(amountKey(amount));
		if (pres) return pres.primaryLabel;
		// Fallback before the reference-currency hook resolves (empty Map on first
		// render). MUST go through formatPrice — it applies normalizeIntlWhitespace so
		// SSR (Node, U+00A0) and first client render (browser, U+202F) emit identical
		// text. A raw Intl.NumberFormat here caused React #418 hydration mismatches.
		return formatPrice(amount, totals.currency, isValidLocale(locale) ? locale : defaultLocale);
	};

	const handleAdd = async () => {
		if (!retaVariant || isPending) return;
		// Line labels double as the transient drawer rows, so the bag shows exactly
		// what the order summary above promised.
		const items = [
			{ variantId: retaVariant.id, productName: t("retaLineItem", { qty }) },
			...(bacVariant && bacVariant.quantityAvailable > 0
				? [{ variantId: bacVariant.id, productName: t("bacLineItem") }]
				: []),
		];

		setIsPending(true);
		let allAdded = true;
		// Serial adds: parallel calls can race checkout creation.
		for (const item of items) {
			// This surface keeps its own toast copy, so the shared one stays off.
			const added = await addToCart(
				{
					channel,
					variantId: item.variantId,
					quantity: 1,
					productName: item.productName,
					// Off the product page, so nothing can refer this add: the homepage
					// bundle block is its own source/surface pair.
					internalSource: "home",
					addSurface: "home_bundle",
				},
				{ suppressToast: true },
			);
			if (!added) {
				allAdded = false;
				break;
			}
		}
		setIsPending(false);

		if (allAdded) toast.success(t("toastSuccess"));
		else toast.error(t("toastError"));
	};

	return (
		<section className="mx-auto max-w-6xl px-4 py-12 sm:px-6 md:py-16 lg:px-8">
			<div className="mb-8 max-w-2xl">
				<span className="mb-2 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-[var(--primary-hero)]">
					<Package className="h-4 w-4" aria-hidden="true" />
					{t("eyebrow")}
				</span>
				<h2 className="text-balance text-3xl font-bold md:text-4xl">
					{t("titleFirst")} <span className="text-emerald-600">{t("titleAccent")}</span>
				</h2>
				<p className="mt-3 text-pretty text-muted-foreground">{t("lead")}</p>
			</div>

			<div className="grid min-w-0 grid-cols-1 overflow-hidden rounded-2xl border border-border bg-card shadow-sm lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
				<div className="flex min-w-0 flex-col gap-3 p-6 md:p-8">
					{([1, 2, 3] as const).map((q) => {
						const variant = pickVariantForQty(bundle.reta, q);
						const price = variant?.pricing?.price?.gross.amount;
						const unitPrice = typeof price === "number" ? price / q : null;
						const selected = q === qty;
						const discountPct =
							typeof singlePackUnit === "number" && singlePackUnit > 0 && unitPrice !== null && q > 1
								? Math.round((1 - unitPrice / singlePackUnit) * 100)
								: 0;
						return (
							<label
								key={q}
								className={`flex min-w-0 cursor-pointer items-start gap-3 rounded-xl border-2 p-4 transition ${
									selected
										? "bg-[var(--surface-primary-soft)]/60 border-[var(--primary-hero)]"
										: "hover:border-[var(--primary-hero)]/50 border-border bg-background"
								}`}
							>
								<input
									type="radio"
									name="qty"
									value={q}
									checked={selected}
									onChange={() => setQty(q)}
									className="mt-1 h-4 w-4 shrink-0 accent-[var(--primary-hero)]"
								/>
								<div className="min-w-0 flex-1">
									<div className="break-words font-semibold">{t("retaTierLabel", { qty: q })}</div>
									<div className="mt-1 flex min-w-0 flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-xs">
										<span className="break-words text-muted-foreground">
											{unitPrice === null ? "—" : t("perVial", { price: formatMoney(unitPrice) })}
										</span>
										<div className="ml-auto break-words text-right text-sm font-semibold">
											{typeof price === "number" ? formatMoney(price) : "—"}
										</div>
									</div>
									<div className="mt-1 min-w-0 break-words text-[11px]">
										{q === 1 ? (
											<span className="text-muted-foreground">{t("listPrice")}</span>
										) : discountPct > 0 ? (
											<span className="inline-flex rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700">
												-{discountPct}%
											</span>
										) : null}
									</div>
								</div>
							</label>
						);
					})}

					<label
						className={`mt-2 flex min-w-0 cursor-pointer items-start gap-3 rounded-xl border-2 p-4 transition ${
							addWater
								? "bg-[var(--surface-primary-soft)]/60 border-[var(--primary-hero)]"
								: "hover:border-[var(--primary-hero)]/50 border-dashed border-border bg-background"
						}`}
					>
						<input
							type="checkbox"
							checked={addWater}
							onChange={(e) => setAddWater(e.target.checked)}
							className="mt-1 h-4 w-4 shrink-0 accent-[var(--primary-hero)]"
						/>
						<div className="min-w-0 flex-1">
							<div className="flex flex-wrap items-center gap-2 font-semibold">
								{t("bacLabel")}
								<span className="bg-muted/60 rounded-md border border-border px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
									{t("recommended")}
								</span>
							</div>
							<div className="mt-1 flex min-w-0 flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-xs">
								<span className="break-words text-muted-foreground">{t("bacSub")}</span>
								<span className="ml-auto break-words text-right text-sm font-semibold">
									{bacVariant ? formatMoney(totals.bacTotal) : "—"}
								</span>
							</div>
						</div>
					</label>
				</div>

				<div className="relative flex min-w-0 flex-col bg-[var(--primary-hero)] p-6 text-white md:p-8">
					<div className="min-w-0 flex-1">
						<div className="text-[11px] font-semibold uppercase tracking-widest text-white/70">
							{tCart("estimatedTotal")}
						</div>
						<div className="mt-2 flex min-w-0 items-baseline gap-1 font-bold leading-none tracking-tight">
							<span className="min-w-0 break-words text-[40px] sm:text-[44px] md:text-[48px]">
								{summary.totalAmount === null ? "—" : formatMoney(summary.totalAmount)}
							</span>
						</div>
						<p className="mt-2 break-words text-xs leading-relaxed text-slate-300">
							{t("shippingPreviewScope")}
						</p>
						{totals.priceReady && totals.savingsVsSinglePacks > 0 ? (
							<div className="mt-1 flex items-center gap-1.5 text-xs font-semibold text-emerald-300">
								<CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
								{t("saved", { amount: formatMoney(totals.savingsVsSinglePacks) })}
							</div>
						) : totals.priceReady ? (
							<div className="mt-1 text-xs text-slate-300">{t("noDiscountYet")}</div>
						) : null}

						<div className="mt-6 grid gap-2 text-sm">
							<div className="flex min-w-0 flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
								<span className="break-words text-slate-300">{tCart("subtotal")}</span>
								<span className="break-words text-right font-semibold">
									{totals.priceReady ? formatMoney(totals.grand) : "—"}
								</span>
							</div>
							<div className="flex min-w-0 flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
								<span className="min-w-0 break-words text-slate-300">{t("retaLineItem", { qty })}</span>
								<span className="break-words text-right font-semibold">
									{retaVariant?.pricing?.price?.gross ? formatMoney(totals.retaTotal) : "—"}
								</span>
							</div>
							{addWater && bacVariant && (
								<div className="flex min-w-0 flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
									<span className="min-w-0 break-words text-slate-300">{t("bacLineItem")}</span>
									<span className="break-words text-right font-semibold">{formatMoney(totals.bacTotal)}</span>
								</div>
							)}
							<div className="flex min-w-0 flex-wrap items-baseline justify-between gap-x-3 gap-y-1 border-t border-white/15 pt-2">
								<Link
									href={`/${locale}/${channel}/shipping`}
									className="break-words text-slate-300 underline underline-offset-2"
								>
									{tCart("shippingEstimateLabel")}
								</Link>
								<span className="break-words text-right font-semibold">
									{summary.shipping.kind === "estimated-paid" && summary.shipping.amount !== null
										? formatMoney(summary.shipping.amount)
										: summary.shipping.kind === "estimated-free"
											? tCart("shippingEstimatedFree")
											: tCart("shippingUnavailable")}
								</span>
							</div>
							{summary.freeShipping.message === "remaining" && (
								<div className="break-words text-xs text-slate-300">
									{tCart("freeShippingProgress", {
										amount: formatMoney(summary.freeShipping.remaining),
									})}
								</div>
							)}
						</div>

						<div className="mt-4 rounded-xl border border-white/10 bg-white/5 p-4">
							<div className="text-[11px] font-bold uppercase tracking-widest text-emerald-300">
								{t("nextTier")}
							</div>
							<div className="mt-1 text-sm">
								{totals.isMaxTier
									? t("maxTierReached")
									: totals.nextTierSavingsPerUnit && totals.nextTierSavingsPerUnit > 0
										? t("nextTierHint", {
												amount: formatMoney(totals.nextTierSavingsPerUnit),
											})
										: t("noDiscountYet")}
							</div>
						</div>
					</div>

					<button
						type="button"
						onClick={() => void handleAdd()}
						disabled={retaOutOfStock || isPending}
						className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-md bg-emerald-500 px-4 py-4 text-base font-bold text-[var(--primary-hero)] shadow-lg transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-60"
					>
						<ShoppingBag className="h-5 w-5" aria-hidden="true" />
						{retaOutOfStock ? t("outOfStock") : isPending ? t("ctaPending") : t("cta")}
					</button>

					<p className="mt-3 flex flex-wrap items-center justify-center gap-1.5 text-center text-[11px] text-slate-300">
						<ShieldCheck className="h-3 w-3" aria-hidden="true" />
						{t("trustRow")}
					</p>
				</div>
			</div>
		</section>
	);
}
